import { useEffect, useState } from "react";

import { Link, Link as RouterLink } from "react-router-dom";
import { AppModel } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { useDocumentData } from "@doitintl/models-hooks";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Stack, Typography } from "@mui/material";

import { FilterTable } from "../../../Components/FilterTable/FilterTable";
import { Loader } from "../../../Components/Loader";
import { useSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import SlidingPanel from "../../../Components/SlidingPanel/SlidingPanel";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { downloadFromBucket } from "../../../utils/firebaseStorage";
import ContractPage from "../ContractView/ContractPage";
import { useCustomerContracts } from "../hooks";
import VendorContractPage from "../VendorContractView/VendorContractPage";
import { filters, headerColumns } from "./columns";
import ContractRow from "./ContractRow";
import { DisabledContractEditingBanner } from "./DisabledContractEditingBanner";
import { type FormattedContract, type FormattedVendorContract } from "./types";
import { contractHasVendorContract, contractType, tabs } from "./utils";

export const ContractsList = () => {
  const { customer } = useCustomerContext();
  const { isDoitEmployee } = useAuthContext();
  const snackbar = useSnackbar();
  const { contracts, loading } = useCustomerContracts();
  const [appContracts] = useDocumentData(getCollection(AppModel).doc("contracts"));
  const [openContractPage, setOpenContractPage] = useState<boolean>(false);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>("customer-contract");
  const [selectedRowFromButton, setSelectedRowFromButton] = useState<string[]>([]);
  const [loadingVendorContract, setLoadingVendorContract] = useState<boolean>(false);
  const [filteredContractsCount, setFilteredContractsCount] = useState<number>(0);

  useEffect(() => {
    setFilteredContractsCount(contracts.length);
  }, [contracts.length]);

  const handleDownload = async (contract: FormattedContract | FormattedVendorContract) => {
    let error;

    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID as string;
    const bucketName = projectId === "me-doit-intl-com" ? "hello-contracts" : `hello-contracts-${projectId}`;

    if (contract?.data.contractFile.storage) {
      error = await downloadFromBucket({
        bucketName,
        path: contract.data.contractFile.storage,
        fileName: contract.data.contractFile.name,
      });

      if (!error) {
        return;
      }
    }

    snackbar.onOpen({
      message: error ?? "Missing file or test environment",
      variant: "error",
      autoHideDuration: 5000,
      withClose: true,
    });
  };

  const handleViewContract = (contractId: string) => {
    setSelectedContractId(contractId);
    setOpenContractPage(true);
    setSelectedTab("customer-contract");
    setSelectedRowFromButton([contractId]);
  };

  const handleCloseContractPage = () => {
    setOpenContractPage(false);
    setSelectedContractId(null);
    setSelectedRowFromButton([]);
  };

  const handleFilterApplied = (filteredItems: readonly FormattedContract[]) => {
    setFilteredContractsCount(filteredItems.length);
  };

  const contractRowWrapper = ({ row }: { row: FormattedContract }) => (
    <ContractRow
      contract={row}
      handleDownload={handleDownload}
      setOpenContractPage={() => {
        handleViewContract(row.id);
      }}
    />
  );

  const panelContent = selectedContractId && (
    <Stack width="100%" maxWidth="1244px" alignItems="center" mx="auto">
      {selectedTab === "customer-contract" && <ContractPage contractId={selectedContractId} isSidePanel />}
      {selectedTab === "vendor-contract" && (
        <VendorContractPage
          contractId={selectedContractId}
          setLoadingVendorContract={setLoadingVendorContract}
          loadingVendorContract={loadingVendorContract}
          isSidePanel
        />
      )}
    </Stack>
  );

  const headerElement = (
    <>
      <Link
        color={!isDoitEmployee ? "text.secondary" : "text.disabled"}
        to={`/customers/${customer.id}/billing/contracts/${selectedContractId}/${selectedTab === "vendor-contract" ? "view-vendor-contract" : "view"}`}
        target="_blank"
      >
        <OpenInNewIcon color="primary" fontSize="small" />
      </Link>
    </>
  );

  const mainPage = (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          mx: 1,
          my: 1,
        }}
      >
        <Typography variant="h1">Contracts</Typography>
        {isDoitEmployee && (
          <Button
            data-cy="create-contract"
            variant="contained"
            aria-label="Create a Contract"
            component={RouterLink}
            to={`/customers/${customer.id}/billing/contracts/create`}
            disabled={appContracts?.disableEditing}
          >
            Create contract
          </Button>
        )}
      </Stack>
      {appContracts?.showDisabledEditingBanner && <DisabledContractEditingBanner />}
      <Loader loading={loading}>
        <FilterTable<FormattedContract>
          showRowsSelection={false}
          tableItems={contracts}
          headerColumns={headerColumns}
          rowComponent={contractRowWrapper}
          filterColumns={filters}
          persistenceKey="contracts_filter"
          defaultSortingColumnValue="data.startDate"
          defaultSortDirection="desc"
          rowDataCyId
          selectedRowsFromButton={selectedRowFromButton}
          onFilterApplied={handleFilterApplied}
        />
      </Loader>
    </Box>
  );

  return (
    <SlidingPanel
      open={openContractPage}
      onClose={handleCloseContractPage}
      panelContent={panelContent}
      panelTitle={contractType(contracts, selectedContractId || "")}
      dataCy="contract-details-panel"
      loading={loadingVendorContract}
      headerElement={headerElement}
      closeOnSmallScreen={true}
      useSidePanelFullHeight={filteredContractsCount < 10}
      headerTabs={
        isDoitEmployee && contractHasVendorContract(contracts, selectedContractId || "", isDoitEmployee)
          ? {
              tabs,
              selectedTab,
              onTabChange: (tab) => {
                setSelectedTab(tab);
                if (tab === "customer-contract") {
                  setLoadingVendorContract(false);
                }
              },
            }
          : undefined
      }
    >
      {mainPage}
    </SlidingPanel>
  );
};

export default ContractsList;
